// TagFilterUtils.js

export function parseTags(raw) {
  if (!raw) return [];

  let parsed = raw;

  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (
    typeof parsed === "object" &&
    !Array.isArray(parsed)
  ) {
    return Object.values(parsed).filter(Array.isArray);
  }

  if (Array.isArray(parsed)) {
    if (Array.isArray(parsed[0])) return parsed;
    return [parsed];
  }

  return [];
}

export function questionMatchesSteps(q, steps) {
  const active = steps.filter(s => s.values && s.values.size > 0);
  if (!active.length) return true;
  const combos = parseTags(q.tags);
  const norm = (v) => (v ?? "").toLowerCase().replace(/\s+/g, "");
  const isScopeVal = (v) => {
    const valNorm = norm(v);
    return valNorm === "scope1" || valNorm === "scope2" || valNorm === "scope3";
  };

  return combos.some(c => {
    let pos = 0;
    for (const step of active) {
      let found = false;
      for (let i = pos; i < c.length; i++) {
        const comboKeyNorm = norm(c[i]?.key);
        const comboValNorm = norm(c[i]?.value);
        const stepKeyNorm = norm(step.key);

        const isKeyMatch = comboKeyNorm === stepKeyNorm || 
                           (stepKeyNorm === "scope" && isScopeVal(c[i]?.value));

        const isValueMatch = [...step.values].some(sv => norm(sv) === comboValNorm);

        if (isKeyMatch && isValueMatch) {
          pos = i + 1;
          found = true;
          break;
        }
      }
      if (!found) return false;
    }
    return true;
  });
}

export function getNextEligibleFilters(allQuestions, filterSteps, savedIds) {
  const active = filterSteps.filter(s => s.values?.size);
  const kvm = new Map();
  const norm = (v) => (v ?? "").toLowerCase().replace(/\s+/g, "");
  const isScopeVal = (v) => {
    const valNorm = norm(v);
    return valNorm === "scope1" || valNorm === "scope2" || valNorm === "scope3";
  };

  for (const q of allQuestions) {
    const combos = parseTags(q.tags);

    for (const combo of combos) {
      if (!combo?.length) continue;

      let matchDepth = 0;
      for (let i = 0; i < active.length; i++) {
        if (i >= combo.length) break;
        const comboKeyNorm = norm(combo[i]?.key);
        const comboValNorm = norm(combo[i]?.value);
        const stepKeyNorm = norm(active[i].key);

        const isKeyMatch = comboKeyNorm === stepKeyNorm || 
                           (stepKeyNorm === "scope" && isScopeVal(combo[i]?.value));

        const isValueMatch = [...active[i].values].some(sv => norm(sv) === comboValNorm);

        if (isKeyMatch && isValueMatch) {
          matchDepth++;
        } else {
          break;
        }
      }

      for (let i = 0; i < active.length; i++) {
        let matchesBefore = true;
        for (let j = 0; j < i; j++) {
          if (j >= combo.length) {
            matchesBefore = false;
            break;
          }
          const comboKeyNorm = norm(combo[j]?.key);
          const comboValNorm = norm(combo[j]?.value);
          const stepKeyNorm = norm(active[j].key);

          const isKeyMatch = comboKeyNorm === stepKeyNorm || 
                             (stepKeyNorm === "scope" && isScopeVal(combo[j]?.value));

          const isValueMatch = [...active[j].values].some(sv => norm(sv) === comboValNorm);

          if (!isKeyMatch || !isValueMatch) {
            matchesBefore = false;
            break;
          }
        }

        if (matchesBefore && combo[i]) {
          const t = combo[i];
          if (!kvm.has(t.key)) kvm.set(t.key, new Set());
          kvm.get(t.key).add(t.value);
        }
      }

      if (matchDepth === active.length && combo[matchDepth]) {
        const nextTag = combo[matchDepth];
        if (!kvm.has(nextTag.key)) kvm.set(nextTag.key, new Set());
        kvm.get(nextTag.key).add(nextTag.value);
      }
    }
  }

  return Array.from(kvm.entries()).map(([key, values]) => ({
    key, values: Array.from(values).sort(),
  })).filter(e => e.values.length > 0);
}

export function getSelectedTagGroups(questions, pendingIds) {
  const chainMap = new Map();
  for (const q of questions) {
    if (!pendingIds.has(q.questionId)) continue;
    for (const combo of parseTags(q.tags)) {
      if (!combo?.length) continue;
      const chainKey = combo.map(t => `${t.key}::${t.value}`).join("|");
      if (!chainMap.has(chainKey)) {
        chainMap.set(chainKey, {
          chain: combo.map(t => ({ key: t.key, value: t.value })),
          kpiIds: new Set(),
        });
      }
      chainMap.get(chainKey).kpiIds.add(q.questionId);
    }
  }
  const groups = new Map();
  for (const entry of chainMap.values()) {
    const topKey = entry.chain[0]?.key;
    if (!topKey) continue;
    if (!groups.has(topKey)) groups.set(topKey, []);
    groups.get(topKey).push(entry);
  }
  return groups;
}

export const DEPTH_COLORS = [
  { bg: "#E6F1FB", text: "#0C447C", border: "#B5D4F4" },
  { bg: "#E1F5EE", text: "#085041", border: "#9FE1CB" },
  { bg: "#FAEEDA", text: "#633806", border: "#FAC775" },
  { bg: "#EEEDFE", text: "#3C3489", border: "#AFA9EC" },
];

export const GROUP_COLORS = [
  { dot: "#378ADD", badge_bg: "#E6F1FB", badge_text: "#0C447C", border: "#B5D4F4" },
  { dot: "#1D9E75", badge_bg: "#E1F5EE", badge_text: "#085041", border: "#9FE1CB" },
  { dot: "#BA7517", badge_bg: "#FAEEDA", badge_text: "#633806", border: "#FAC775" },
  { dot: "#7F77DD", badge_bg: "#EEEDFE", badge_text: "#3C3489", border: "#AFA9EC" },
];

export function formatTagKey(key) {
  if (!key) return key;
  const spaced = key.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.replace(/\b\w/g, c => c.toUpperCase());
}
